import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = signed in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("vexis_token");
    if (!token) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("vexis_token", data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name, referralCode) => {
    setError("");
    try {
      const payload = { email, password, name };
      if (referralCode) payload.referral_code = referralCode;
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("vexis_token", data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("vexis_token");
    setUser(false);
  };

  const setChips = (chips) => {
    setUser((u) => (u && typeof u === "object" ? { ...u, chips } : u));
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh, setChips, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
